import { ApiError } from '../lib/api';
import { useCodeLabel, useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime } from '../lib/format';
import { useCustomerPasses, useIssueWalletPass } from '../lib/queries';
import { Alert, Button, Card, Pill, SectionTitle } from './ui';

const LABEL = { APPLE: 'Apple Wallet', GOOGLE: 'Google Wallet' } as const;

export function WalletIssue({ customerId }: { customerId: string }) {
  const { t } = useI18n();
  const code = useCodeLabel();
  const { data: passes } = useCustomerPasses(customerId);
  const issue = useIssueWalletPass(customerId);
  const errorText = useApiError();
  const error = issue.error instanceof ApiError ? issue.error : null;

  const live = (passes ?? []).filter((pass) => !pass.revokedAt);

  return (
    <Card>
      <SectionTitle>{t('walletIssue.title')}</SectionTitle>

      {error && <div className="mb-3"><Alert>{errorText(error) ?? error.message}</Alert></div>}

      {live.length === 0 ? (
        <p className="text-sm leading-relaxed text-ink-dim">{t('walletIssue.none')}</p>
      ) : (
        <div className="mb-3 space-y-1.5">
          {live.map((pass) => (
            <div
              key={pass.id}
              className="flex items-center justify-between gap-2 rounded-lg border border-line bg-raised px-3 py-2"
            >
              <span className="min-w-0">
                <span className="block text-sm text-ink">{LABEL[pass.provider]}</span>
                <span className="block text-[0.68rem] text-ink-faint">
                  {pass.lastSyncedAt
                    ? t('walletIssue.synced', { when: formatDateTime(pass.lastSyncedAt) })
                    : t('walletIssue.notIssued')}
                </span>
              </span>
              <Pill tone={pass.status === 'ERROR' ? 'danger' : pass.status === 'NOT_ISSUED' ? 'neutral' : 'positive'}>
                {code('status', pass.status)}
              </Pill>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {(['APPLE', 'GOOGLE'] as const).map((provider) => {
          const existing = live.find((pass) => pass.provider === provider);
          const settled = existing && existing.status !== 'ERROR' && existing.status !== 'NOT_ISSUED';
          return (
            <Button
              key={provider}
              variant="ghost"
              size="sm"
              disabled={settled}
              loading={issue.isPending && issue.variables?.[0] === provider}
              onClick={() => issue.mutate([provider])}
            >
              {existing?.status === 'ERROR'
                ? t('walletIssue.retry', { provider: LABEL[provider] })
                : t('walletIssue.issue', { provider: LABEL[provider] })}
            </Button>
          );
        })}
      </div>

    </Card>
  );
}
